import type { components } from './schema'
import { getAccessToken } from '../auth/msal'

/**
 * Typed API client.
 *
 * Every type here comes from `schema.d.ts`, which is generated from the running
 * API's OpenAPI document. Nothing in this file re-declares a shape by hand — that
 * is the whole point. Change a C# record and `npm run build` fails, instead of the
 * mismatch surfacing as an undefined at runtime in front of a user.
 *
 * Regenerate with: npm run api:generate (API must be running).
 */

type Schemas = components['schemas']

export type VehicleGridRow = Schemas['VehicleGridRow']
export type VehicleGridPage = Schemas['VehicleGridRowPagedResult']
export type VehicleDetail = Schemas['VehicleDetail']
export type ComplianceCell = Schemas['ComplianceCell']
export type ComplianceDetail = Schemas['ComplianceDetail']
export type ComplianceEditRequest = Schemas['ComplianceEditRequest']
export type ActivityEntry = Schemas['ActivityEntry']
export type ActivityPage = Schemas['ActivityEntryPagedResult']
export type ComplianceHistoryEntry = Schemas['ComplianceHistoryEntry']
export type DueItem = Schemas['DueItem']
export type ImportSummary = Schemas['ImportSummary']
export type ImportRowOutcome = Schemas['ImportRowOutcome']
export type ReferenceData = Schemas['ReferenceData']
export type RegistryFacets = Schemas['RegistryFacets']
export type DashboardRegistry = Schemas['DashboardRegistry']
export type RegistryFacet = Schemas['RegistryFacet']
export type RegistrySnapshotView = Schemas['RegistrySnapshotView']
export type RegistrySnapshotObligation = Schemas['RegistrySnapshotObligation']
export type CreateVehicleRequest = Schemas['CreateVehicleRequest']
export type VehicleEditRequest = Schemas['VehicleEditRequest']
export type VehicleReport = Schemas['VehicleReport']
export type ReportVehicle = Schemas['ReportVehicle']
export type ReportObligation = Schemas['ReportObligation']
export type ReportAction = Schemas['ReportAction']
export type ReportTotals = Schemas['ReportTotals']
export type DocumentSummary = Schemas['DocumentSummary']
export type UploadResult = Schemas['UploadResult']
export type RegisterOutcome = Schemas['RegisterOutcome']
export type ReminderRule = Schemas['ReminderRule']
export type ReminderSentRecord = Schemas['ReminderSentRecord']
export type ReminderSentPage = Schemas['ReminderSentRecordPagedResult']
export type ReminderRunOutcome = Schemas['ReminderRunOutcome']
export type SaveReminderRuleRequest = Schemas['SaveReminderRuleRequest']
export type ReminderPreview = Schemas['ReminderPreview']
export type NotificationItem = Schemas['NotificationItem']
export type NotificationPage = Schemas['NotificationItemPagedResult']
export type AskAnswer = Schemas['AskAnswer']
export type AskRow = Schemas['AskRow']
export type ExportColumnGroup = Schemas['ExportColumnGroup']
export type ExportColumnInfo = Schemas['ExportColumnInfo']
export type ExportCount = Schemas['ExportCount']
export type ExportRequest = Schemas['ExportRequest']

export type EmailStatusResponse = Schemas['EmailStatusResponse']
export type EmailRecipient = Schemas['EmailRecipient']
export type EmailDeliveryRecord = Schemas['EmailDeliveryRecord']
export type EmailDeliveryPage = Schemas['EmailDeliveryRecordPagedResult']
export type SendReportRequest = Schemas['SendReportRequest']

export type DashboardOverview = Schemas['DashboardOverview']
export type DashboardTotals = Schemas['DashboardTotals']
export type DashboardObligation = Schemas['DashboardObligation']
export type DashboardRunwayCell = Schemas['DashboardRunwayCell']
export type DashboardSite = Schemas['DashboardSite']
export type DashboardVehicleType = Schemas['DashboardVehicleType']
export type DashboardRegistrationYear = Schemas['DashboardRegistrationYear']
export type DashboardStaleness = Schemas['DashboardStaleness']
export type DashboardFinding = Schemas['DashboardFinding']
export type IntakeItem = Schemas['IntakeItem']
export type IntakePage = Schemas['IntakeItemPagedResult']
export type IntakeStatusResponse = Schemas['IntakeStatusResponse']
export type AcceptIntakeRequest = Schemas['AcceptIntakeRequest']

/**
 * The four states an expiry can be in.
 *
 * OpenAPI renders C# enums as bare numeric unions (`1 | 2 | 3 | 4`), which is
 * type-safe but unreadable. These named constants restore the meaning without
 * re-declaring the type — `Status.NoExpiry` instead of `2`.
 */
export const Status = {
  Known: 1,
  NoExpiry: 2,
  NotApplicable: 3,
  Unknown: 4,
} as const satisfies Record<string, Schemas['ComplianceStatus']>

/**
 * Severity bands, ordered by urgency. Mirrors dbo.fn_ComplianceSeverity and
 * SeverityCalculator.
 *
 * NoExpiry sits BELOW Valid deliberately: a lifetime tax never needs action, so
 * it must never out-rank a date that is merely fine for now.
 */
export const Severity = {
  NotApplicable: 0,
  NoExpiry: 1,
  Valid: 2,
  DueSoon: 3,
  Unknown: 4,
  Expired: 5,
} as const satisfies Record<string, Schemas['ComplianceSeverity']>

/**
 * The type behind the constants above. Declared with the same name deliberately —
 * TypeScript keeps types and values in separate namespaces, so `Severity.Expired`
 * (value) and `severity: Severity` (type) both work without a second name to keep
 * in step.
 */
export type Severity = Schemas['ComplianceSeverity']

export const Source = {
  Manual: 1,
  ExcelImport: 2,
  RegistryApi: 3,
} as const satisfies Record<string, Schemas['UpdateSource']>

/** Thrown when the server rejects a request. Carries the status so callers can branch. */
export class ApiError extends Error {
  // Declared explicitly rather than as a constructor parameter property: the
  // project builds with `erasableSyntaxOnly`, which only permits TypeScript
  // syntax that erases to nothing at runtime.
  readonly status: number

  constructor(status: number, message: string) {
    super(message)
    this.name = 'ApiError'
    this.status = status
  }

  /**
   * Someone else changed the record first. Not a fault — the user reloads and
   * retries. Surfaced as its own flag so the UI can say so plainly rather than
   * showing a generic failure.
   */
  get isConflict() {
    return this.status === 409
  }

  /** No network, or the request never reached the server. */
  get isOffline() {
    return this.status === 0
  }
}

/**
 * Where the API lives.
 *
 * Relative in development, where Vite proxies /api to localhost:5199 so the app
 * is same-origin and cookies and CORS behave as they will in production.
 *
 * Absolute in a deployed build, because the PWA is served from Static Web Apps
 * and the API from App Service — different origins. Set VITE_API_BASE at build
 * time; the CORS allow-list on the API must name the site's origin to match.
 */
const BASE = import.meta.env.VITE_API_BASE
  ? `${String(import.meta.env.VITE_API_BASE).replace(/\/$/, '')}/api`
  : '/api'

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  let response: Response

  // Every endpoint requires a signed-in user, so the token rides on every call.
  // Null when the build has no Entra configuration — the local DevAuth API
  // authenticates regardless, and a real one correctly refuses.
  const token = await getAccessToken()

  try {
    response = await fetch(`${BASE}${path}`, {
      ...init,
      headers: {
        Accept: 'application/json',
        ...(init?.body instanceof FormData ? {} : { 'Content-Type': 'application/json' }),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...init?.headers,
      },
    })
  } catch {
    // fetch rejects only on network failure, never on an HTTP error status.
    // Status 0 marks that distinction so the UI can offer "you appear to be
    // offline" rather than a server error the server never sent.
    throw new ApiError(0, 'Cannot reach the server. Check your connection.')
  }

  if (!response.ok) {
    /*
      A 401 is almost never "the server rejected you" here — every endpoint is
      behind the same policy and the page would not have loaded at all. It is a
      token the client failed to attach: the silent refresh expired and the
      sign-in popup was blocked, so the request went out with no Authorization
      header and the API refused it correctly.

      "Request failed (401)" told a user nothing they could act on.
    */
    if (response.status === 401) {
      throw new ApiError(
        401,
        'Your session has expired. Reload the page to sign in again, then try once more.',
      )
    }

    let message = `Request failed (${response.status}).`

    try {
      const body = await response.json()
      if (typeof body?.message === 'string') message = body.message
    } catch {
      // A non-JSON error body is not itself an error worth reporting; the status
      // code is already the useful signal.
    }

    throw new ApiError(response.status, message)
  }

  if (response.status === 204) return undefined as T

  return (await response.json()) as T
}

/** One machine, as the register lists it. */
export interface Machine {
  machineryId: number
  machineryCategoryId: number
  categoryName: string
  assetCode: string | null
  serialNumber: string | null
  registrationNumberCanonical: string | null
  make: string | null
  model: string | null
  manufactureYear: number | null
  siteNameRaw: string | null
  altSiteName: string | null
  remarks: string | null
  sourceSerialNo: number | null
  /** Something that identifies or dates this machine is missing. */
  needsAttention: boolean
}

export interface MachineDetail extends Machine {
  siteId: number | null
  siteName: string | null
  remarksAlt: string | null
  isDeleted: boolean
  createdAtUtc: string
  createdBy: string
  modifiedAtUtc: string | null
  modifiedBy: string | null
  rowVersion: string | null
  /**
   * A LIVE vehicle carrying the same plate, resolved on every read rather than
   * stored — so it appears when that vehicle is added and disappears when it is
   * removed.
   */
  vehicleId: number | null
  vehicleTypeName: string | null
}

export interface MachineryFacet {
  name: string
  machineCount: number
  id: number | null
}

export interface MachineryFacets {
  totals: {
    totalMachines: number
    categories: number
    sites: number
    withPlate: number
    needsAttention: number
  }
  categories: MachineryFacet[]
  sites: MachineryFacet[]
  makes: MachineryFacet[]
  years: MachineryFacet[]
}

export interface MachineryQuery {
  page?: number
  pageSize?: number
  search?: string
  machineryCategoryId?: number
  siteName?: string
  make?: string
  yearFrom?: number
  yearTo?: number
  hasPlate?: boolean
  needsAttention?: boolean
  sortBy?: string
  sortDescending?: boolean
}

/** What a machine's form sends. Null means blank, not "leave it". */
export interface MachineryInput {
  categoryName: string
  assetCode?: string | null
  serialNumber?: string | null
  registrationNumberCanonical?: string | null
  make?: string | null
  model?: string | null
  manufactureYear?: number | null
  siteNameRaw?: string | null
  altSiteName?: string | null
  remarks?: string | null
  remarksAlt?: string | null
}

export interface MachineryImportSummary {
  importBatchId: number
  totalSourceRows: number
  created: number
  updated: number
  ambiguous: number
  rejected: number
  warningRows: number
  categoriesCreated: number
  needsAttention: number
}

export interface MachineryImportRow {
  sourceRowNumber: number
  sourceLabel: string | null
  outcome: 1 | 2 | 3 | 4 | 5
  columnName: string | null
  rawValue: string | null
  reason: string | null
  machineryId: number | null
}

/** Which machines an export covers. Mirrors MachineryExportScope. */
export const MachineryScope = {
  All: 1,
  Filter: 2,
  Selected: 3,
} as const

export type MachineryScopeValue = (typeof MachineryScope)[keyof typeof MachineryScope]

export interface MachineryExportRequest {
  scope: MachineryScopeValue
  columns?: string[] | null
  machineryIds?: number[] | null
  filter?: MachineryQuery | null
}

export interface MachineryExportColumnGroup {
  group: string
  columns: { key: string; header: string }[]
}

export interface MachineryExportCount {
  rows: number
  truncated: boolean
  maxRows: number
}

/** Mirrors PagedResult<T>, which the generated schema does not name generically. */
export interface Paged<T> {
  items: T[]
  page: number
  pageSize: number
  totalRows: number
  totalPages: number
}

/**
 * What an import row became. Mirrors MachineryRowOutcome, which mirrors
 * CK_MachineryImportRow_Outcome.
 */
export const MachineryOutcome = {
  Imported: 1,
  ImportedWithWarnings: 2,
  Rejected: 3,
  Updated: 4,
  Ambiguous: 5,
} as const

export interface GridQuery {
  page?: number
  pageSize?: number
  siteId?: number
  vehicleTypeId?: number
  search?: string

  /**
   * EXACT worst-state band, not a minimum. The bands partition the fleet, so the
   * counts add up — a ">=" filter returned almost everything when 608 of 609
   * vehicles are already expired.
   */
  worstSeverity?: Schemas['ComplianceSeverity']

  /** "Has any obligation in this state", regardless of the row's worst band. */
  hasSeverity?: Schemas['ComplianceSeverity']

  /** This specific obligation is in this state — what clicking the summary strip sets. */
  obligationTypeId?: number
  obligationSeverity?: Schemas['ComplianceSeverity']

  /**
   * Expiry window — "what falls due in October", the dashboard runway's
   * drill-down. ISO yyyy-MM-dd.
   *
   * Scoped to obligationTypeId when one is set, otherwise any obligation falling
   * due inside it. Only dated values match, so the other three states are
   * excluded rather than guessed at.
   */
  expiryFrom?: string
  expiryTo?: string

  /** Multiple sites, serialised as a comma-separated list. */
  siteIds?: number[]

  /**
   * An explicit list of registrations — "these forty, and nothing else".
   *
   * Matched EXACTLY on the server, not as a prefix like `search`: pasting a list is
   * a deliberate lookup, and a prefix match would turn "MH12" into hundreds of rows.
   * Canonicalisation (case, spaces, dashes, dots, slashes) happens in the procedure,
   * with the same rule the single-term search uses.
   */
  registrations?: string[]

  /** What VAHAN says about the vehicle itself. Each null/omitted means unfiltered. */
  fuelType?: string
  rcStatus?: string
  isFinanced?: boolean
  financer?: string
  emissionNorms?: string

  /** Restrict to vehicles that do (RegistryApi) or do not carry registry data. */
  source?: Schemas['UpdateSource']

  /** Only vehicles carrying at least one hand-corrected value. */
  onlyOverridden?: boolean

  /** Certificates on file: 1 = has at least one, 2 = has none. */
  documentFilter?: 1 | 2

  /**
   * Scopes documentFilter to one obligation. Omitted means "across every
   * obligation that takes a certificate" — including All India Permit, which has
   * no grid column but does take one.
   */
  documentTypeId?: number

  sortBy?: string
  sortDescending?: boolean
}

/** What the document filter is asking. */
export const DocumentFilter = {
  HasAny: 1,
  HasNone: 2,
} as const

function toQueryString(params: Record<string, unknown>): string {
  const search = new URLSearchParams()

  for (const [key, value] of Object.entries(params)) {
    // Empty string is a meaningful "cleared filter", not a value to send —
    // sending it would filter the grid to vehicles whose name is empty.
    if (value === undefined || value === null || value === '') continue
    search.set(key, String(value))
  }

  const qs = search.toString()
  return qs ? `?${qs}` : ''
}

export const api = {
  getVehicles: (query: GridQuery = {}) =>
    request<VehicleGridPage>(
      // siteIds is flattened to CSV because a query string has no array type and
      // the server splits it with STRING_SPLIT — parameterised throughout.
      `/vehicles${toQueryString({
        ...query,
        siteIds: query.siteIds?.join(','),
        registrations: query.registrations?.join(','),
      })}`,
    ),

  getVehicle: (vehicleId: number) => request<VehicleDetail>(`/vehicles/${vehicleId}`),

  /**
   * Everything the registry said about one vehicle.
   *
   * Returns null when nothing is held — the server answers 204 rather than an empty
   * object, because "we have never asked" and "the registry had nothing to say" look
   * identical in an empty payload and only one is worth acting on.
   */
  getRegistrySnapshot: (vehicleId: number) =>
    request<RegistrySnapshotView | null>(`/registry/snapshot/${vehicleId}`),

  /** The archived payload, verbatim. Null when it was kept inline or the blob is gone. */
  getRegistryRawResponse: (vehicleId: number) =>
    request<unknown | null>(`/registry/response/${vehicleId}`),

  /**
   * Adds a vehicle.
   *
   * `wasRestored` says the registration was one that had been REMOVED and has been
   * brought back — the same row, with its documents, history and activity log
   * intact. Worth telling the person: they pressed "add" and got a populated old
   * record rather than the blank new one they were picturing.
   */
  createVehicle: (body: CreateVehicleRequest) =>
    request<{ vehicleId: number; wasRestored: boolean }>('/vehicles', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  /**
   * Updates a vehicle's own fields.
   *
   * A blank field means blank here, unlike an import — clearing the asset code is
   * taken at face value rather than ignored.
   */
  editVehicle: (vehicleId: number, body: VehicleEditRequest) =>
    request<void>(`/vehicles/${vehicleId}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),

  getReference: () => request<ReferenceData>('/reference'),

  /**
   * Duplicate check for the add form. Returns null (not a thrown error) when the
   * registration is new — "not found" is the expected answer most of the time,
   * so it should not read as a failure.
   */
  findByRegistration: async (registration: string) => {
    try {
      return await request<Schemas['VehicleLookup']>(
        `/vehicles/by-registration/${encodeURIComponent(registration)}`,
      )
    } catch (error) {
      if (error instanceof ApiError && error.status === 404) return null
      throw error
    }
  },

  getRegistryStatus: () => request<Schemas['RegistryStatus']>('/registry/status'),

  syncFromRegistry: (vehicleId: number) =>
    request<Schemas['SyncResult']>(`/registry/sync/${vehicleId}`, { method: 'POST' }),

  editCompliance: (vehicleId: number, complianceTypeId: number, body: ComplianceEditRequest) =>
    request<void>(`/vehicles/${vehicleId}/compliance/${complianceTypeId}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),

  deleteVehicle: (vehicleId: number, rowVersion: string) =>
    request<void>(`/vehicles/${vehicleId}${toQueryString({ rowVersion })}`, { method: 'DELETE' }),

  getActivity: (vehicleId: number, page = 1, pageSize = 50) =>
    request<ActivityPage>(`/vehicles/${vehicleId}/activity${toQueryString({ page, pageSize })}`),

  getHistory: (vehicleId: number, asOfUtc?: string) =>
    request<ComplianceHistoryEntry[]>(`/vehicles/${vehicleId}/history${toQueryString({ asOfUtc })}`),

  getDue: (days = 30, complianceTypeId?: number, siteId?: number) =>
    request<DueItem[]>(`/compliance/due${toQueryString({ days, complianceTypeId, siteId })}`),

  getComplianceSummary: (siteId?: number, vehicleTypeId?: number) =>
    request<Schemas['ComplianceSummaryRow'][]>(
      `/compliance/summary${toQueryString({ siteId, vehicleTypeId })}`,
    ),

  importWorkbook: (file: File, sheetName = 'VEHICLE') => {
    const form = new FormData()
    form.append('file', file)

    return request<ImportSummary>(`/imports${toQueryString({ sheetName })}`, {
      method: 'POST',
      body: form,
    })
  },

  getImportReport: (batchId: number) =>
    request<ImportRowOutcome[]>(`/imports/${batchId}/report`),

  getReport: (vehicleIds: number[]) =>
    request<VehicleReport>(`/reports/vehicles${toQueryString({ ids: vehicleIds.join(',') })}`),

  /**
   * URL for the Excel workbook.
   *
   * A URL rather than a fetch: letting the browser download it means a real
   * save dialog, a real progress indication and no blob held in memory.
   */
  reportWorkbookUrl: (vehicleIds: number[]) =>
    `${BASE}/reports/vehicles.xlsx${toQueryString({ ids: vehicleIds.join(',') })}`,

  getDocuments: (vehicleId: number) =>
    request<DocumentSummary[]>(`/vehicles/${vehicleId}/documents`),

  /**
   * Attaches a scanned certificate to one obligation.
   *
   * Multipart rather than JSON because the date read off the certificate travels
   * with the file. Splitting them across two requests would let the scan land
   * without the date that gives it its point.
   */
  uploadDocument: (
    vehicleId: number,
    complianceTypeId: number,
    file: File,
    options: { documentExpiryDate?: string; notes?: string; applyExpiryToRegister?: boolean } = {},
  ) => {
    const form = new FormData()
    form.append('file', file)

    // ISO only, and only when set. The server parses with an explicit format for
    // the reason the whole codebase does: 03-04-2026 is 3 April here and 4 March
    // under a US locale, and the wrong one is a plausible-looking wrong date.
    if (options.documentExpiryDate) form.append('documentExpiryDate', options.documentExpiryDate)
    if (options.notes) form.append('notes', options.notes)
    if (options.applyExpiryToRegister) form.append('applyExpiryToRegister', 'true')

    return request<UploadResult>(`/vehicles/${vehicleId}/documents/${complianceTypeId}`, {
      method: 'POST',
      body: form,
    })
  },

  deleteDocument: (vehicleId: number, documentId: number) =>
    request<void>(`/vehicles/${vehicleId}/documents/${documentId}`, { method: 'DELETE' }),

  /**
   * URL for a document's bytes.
   *
   * Only usable where a bearer token is not required — a DevAuth build. A plain
   * <a href> cannot carry an Authorization header, so against a real API this
   * returns 401. Use openDocument/saveDocument instead; this stays for the local
   * development case and for constructing the request inside those.
   */
  documentContentUrl: (vehicleId: number, documentId: number, download = false) =>
    `${BASE}/vehicles/${vehicleId}/documents/${documentId}/content${
      download ? '?download=true' : ''
    }`,

  /**
   * Fetches a document with the caller's token and hands back a blob URL.
   *
   * The indirection exists because authorisation is decided per request, by
   * design — content is streamed through the API rather than served from storage
   * — and a browser navigating to a URL sends no Authorization header. So the
   * bytes are fetched properly and handed to the viewer as a local object URL.
   *
   * The caller must revokeObjectURL when finished, or the blob is held for the
   * life of the page.
   */
  fetchDocumentBlobUrl: async (vehicleId: number, documentId: number) => {
    const token = await getAccessToken()
    const response = await fetch(
      `${BASE}/vehicles/${vehicleId}/documents/${documentId}/content`,
      {
        credentials: 'same-origin',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      },
    )

    if (!response.ok) {
      throw new ApiError(response.status, 'Could not open that document.')
    }

    return URL.createObjectURL(await response.blob())
  },

  // ---------------------------------------------------------------
  // Reminders
  // ---------------------------------------------------------------

  getReminderRules: (includeDisabled = true, includeVehicleRules = false) =>
    request<ReminderRule[]>(
      `/reminders${toQueryString({ includeDisabled, includeVehicleRules })}`,
    ),

  /**
   * One vehicle's own reminders AND the fleet rules that already cover it.
   *
   * Both, in one call, because the overlap is the thing worth seeing: a vehicle
   * under a fleet rule and carrying its own gets two emails, and the screen has
   * to say so before somebody adds a third.
   */
  getRemindersForVehicle: (vehicleId: number) =>
    request<ReminderRule[]>(`/reminders/vehicle/${vehicleId}`),

  /** What this rule would actually say today — the editor seeds itself from it. */
  previewReminder: (reminderRuleId: number) =>
    request<ReminderPreview>(`/reminders/${reminderRuleId}/preview`),

  /**
   * Up to fifty rules in one round trip.
   *
   * Setting a reminder on every obligation of a vehicle is one gesture, and
   * seven sequential POSTs against a Basic-tier database is seconds of waiting
   * for something that should feel instant.
   */
  saveReminderRulesBulk: (body: SaveReminderRuleRequest[]) =>
    request<{
      saved: number
      failed: number
      results: { ok: boolean; reminderRuleId: number; message: string; name: string }[]
    }>('/reminders/bulk', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  saveReminderRule: (body: SaveReminderRuleRequest) =>
    request<{ reminderRuleId: number; message: string }>('/reminders', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  deleteReminderRule: (reminderRuleId: number) =>
    request<void>(`/reminders/${reminderRuleId}`, { method: 'DELETE' }),

  getReminderHistory: (ruleId?: number, page = 1, pageSize = 50) =>
    request<ReminderSentPage>(`/reminders/history${toQueryString({ ruleId, page, pageSize })}`),

  /**
   * Run every enabled rule now.
   *
   * Safe to call repeatedly: the server records what it has already reminded
   * about, so a second run sends nothing the first one covered. That is what
   * makes this offerable as a button at all.
   */
  runReminders: () =>
    request<ReminderRunOutcome>('/reminders/run', { method: 'POST' }),

  // ---------------------------------------------------------------------
  // Notifications
  //
  // Scoped to the signed-in user by the server, never by a parameter here: a
  // userId on the wire would let one person clear another person's bell, and
  // this system has no roles to prevent it.
  // ---------------------------------------------------------------------

  getNotifications: (includeCleared = false, page = 1, pageSize = 100) =>
    request<NotificationPage>(
      `/notifications${toQueryString({ includeCleared, page, pageSize })}`,
    ),

  getNotificationCount: () =>
    request<{ unreadCount: number }>('/notifications/count'),

  // ---------------------------------------------------------------------
  // Ask
  //
  // Read-only. The question is matched to one of six typed tools over the
  // procedures the screens already use — never turned into SQL — so an answer
  // and the register cannot disagree about a number.
  // ---------------------------------------------------------------------

  ask: (question: string) =>
    request<AskAnswer>('/ask', {
      method: 'POST',
      body: JSON.stringify({ question }),
    }),

  getAskCapabilities: () =>
    request<{ router: string; examples: string[] }>('/ask/capabilities'),

  // ---------------------------------------------------------------------
  // Export
  //
  // The column list comes from the server, never from a copy kept here: the
  // writer emits what that registry contains, so a locally-held list is how
  // somebody ticks a box and gets an empty column back.
  // ---------------------------------------------------------------------

  getExportColumns: () => request<ExportColumnGroup[]>('/exports/columns'),

  /** How many rows this scope would produce, before anything is generated. */
  countExport: (body: ExportRequest) =>
    request<ExportCount>('/exports/count', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  /**
   * The workbook itself.
   *
   * Returns the blob rather than triggering a download, because the caller
   * decides what to do with it — save it, or hand it to the share sheet.
   */
  exportWorkbook: async (body: ExportRequest): Promise<Blob> => {
    // Not through request<T>(): that parses JSON, and this is a binary file.
    // The token and the base URL are resolved the same way, so the two cannot
    // authenticate differently.
    const token = await getAccessToken()

    const response = await fetch(`${BASE}/exports/vehicles.xlsx`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(body),
    })

    if (!response.ok) {
      // The failure body IS json even though the success body is not.
      const message = await response
        .json()
        .then((b: { message?: string }) => b.message)
        .catch(() => null)
      throw new ApiError(response.status, message ?? 'The export could not be produced.')
    }

    return response.blob()
  },

  emailExport: (toAddress: string, exportRequest: ExportRequest) =>
    request<{ emailDeliveryId: number; rows: number; fileName: string }>('/exports/email', {
      method: 'POST',
      body: JSON.stringify({ toAddress, export: exportRequest }),
    }),

  // ---------------------------------------------------------------------
  // Machinery
  //
  // A separate register, not a filter on the vehicle one. Plant carries no
  // obligations — the Asset List has no insurance, fitness, permit, tax or PUC
  // column for it — so none of the compliance plumbing applies.
  //
  // Types are hand-written here rather than pulled from schema.d.ts because the
  // generated client has not been regenerated against these endpoints yet; run
  // `npm run api:generate` and they can be replaced by the generated ones.
  // ---------------------------------------------------------------------

  getMachinery: (query: MachineryQuery) =>
    request<Paged<Machine>>(`/machinery${toQueryString(query as Record<string, unknown>)}`),

  /**
   * Filter options and totals, counted from the machinery itself.
   *
   * Never a hardcoded list: a category the client adds next month is filterable
   * without a deployment, and 'SOIL STABILIAXER' appears exactly as they spell it.
   */
  getMachineryFacets: () => request<MachineryFacets>('/machinery/facets'),

  getMachine: (machineryId: number) => request<MachineDetail>(`/machinery/${machineryId}`),

  createMachine: (body: MachineryInput) =>
    request<{ machineryId: number; wasRestored: boolean }>('/machinery', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  updateMachine: (machineryId: number, machine: MachineryInput, rowVersion: string) =>
    request<void>(`/machinery/${machineryId}`, {
      method: 'PUT',
      body: JSON.stringify({ machine, rowVersion }),
    }),

  deleteMachine: (machineryId: number, rowVersion: string) =>
    request<void>(`/machinery/${machineryId}?rowVersion=${encodeURIComponent(rowVersion)}`, {
      method: 'DELETE',
    }),

  importMachinery: (file: File, sheetName = 'Asset List') => {
    const form = new FormData()
    form.append('file', file)

    return request<MachineryImportSummary>(
      `/machinery/import?sheetName=${encodeURIComponent(sheetName)}`,
      { method: 'POST', body: form },
    )
  },

  /**
   * Who added this machine, who edited it, and what they changed.
   *
   * Its own endpoint, not the vehicle one: that filters on ActivityLog.VehicleId
   * and a machinery entry has none — it is keyed on EntityType and EntityId.
   */
  getMachineActivity: (machineryId: number, page = 1, pageSize = 50) =>
    request<Paged<ActivityEntry>>(
      `/machinery/${machineryId}/activity?page=${page}&pageSize=${pageSize}`,
    ),

  getMachineryImportReport: (batchId: number) =>
    request<MachineryImportRow[]>(`/machinery/imports/${batchId}/report`),

  // ---------------------------------------------------------------------
  // Machinery export
  //
  // Its own endpoints, not a data set on /exports: the vehicle export takes
  // severities, expiry windows and document filters, and machinery has no
  // concept of any of them.
  // ---------------------------------------------------------------------

  getMachineryExportColumns: () =>
    request<MachineryExportColumnGroup[]>('/machinery/exports/columns'),

  /** How many rows this scope would produce, before anything is generated. */
  countMachineryExport: (body: MachineryExportRequest) =>
    request<MachineryExportCount>('/machinery/exports/count', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  /**
   * The workbook itself.
   *
   * Returns the blob rather than triggering a download, because the caller
   * decides what to do with it — save it, or hand it to the share sheet.
   */
  exportMachineryWorkbook: async (body: MachineryExportRequest): Promise<Blob> => {
    // Not through request<T>(): that parses JSON, and this is a binary file. The
    // token and base URL resolve the same way, so the two cannot authenticate
    // differently.
    const token = await getAccessToken()

    const response = await fetch(`${BASE}/machinery/exports/machinery.xlsx`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(body),
    })

    if (!response.ok) {
      // The failure body IS json even though the success body is not.
      const message = await response
        .json()
        .then((b: { message?: string }) => b.message)
        .catch(() => null)
      throw new ApiError(response.status, message ?? 'The export could not be produced.')
    }

    return response.blob()
  },

  emailMachineryExport: (toAddress: string, exportRequest: MachineryExportRequest) =>
    request<{ emailDeliveryId: number; rows: number; fileName: string }>(
      '/machinery/exports/email',
      { method: 'POST', body: JSON.stringify({ toAddress, export: exportRequest }) },
    ),

  /** An empty list clears everything — the Clear button. */
  dismissNotifications: (reminderSentIds: number[] = []) =>
    request<{ cleared: number }>('/notifications/dismiss', {
      method: 'POST',
      body: JSON.stringify({ reminderSentIds }),
    }),

  restoreNotifications: (reminderSentIds: number[] = []) =>
    request<{ restored: number }>('/notifications/restore', {
      method: 'POST',
      body: JSON.stringify({ reminderSentIds }),
    }),

  // ---------------------------------------------------------------
  // Emailing a report
  // ---------------------------------------------------------------

  /**
   * Whether reports can be emailed at all, and from what address.
   *
   * Asked before the control is offered. Without a relay the Send button is
   * disabled with a reason — a button that fails when pressed is worse than one
   * that explains itself.
   */
  getEmailStatus: () => request<EmailStatusResponse>('/email/status'),

  /**
   * Addresses used before, for autofill.
   *
   * Comes from the delivery history rather than a contacts list, so there is one
   * source of truth and it cannot drift.
   */
  getEmailRecipients: (search?: string, topN = 20) =>
    request<EmailRecipient[]>(`/email/recipients${toQueryString({ search, topN })}`),

  getEmailHistory: (page = 1, pageSize = 50) =>
    request<EmailDeliveryPage>(`/email/history${toQueryString({ page, pageSize })}`),

  /** Sends the report as a PDF attachment and records the delivery. */
  sendReportEmail: (body: SendReportRequest) =>
    request<{ emailDeliveryId: number; message: string }>('/email/report', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  // ---------------------------------------------------------------
  // Dashboard
  // ---------------------------------------------------------------

  /**
   * Everything the landing page shows, in one call.
   *
   * One request rather than one per panel, because every figure has to agree on
   * a single as-of date. Split across calls, two of them can straddle midnight
   * and the screen contradicts itself.
   */
  getDashboard: (asOfDate?: string, monthsAhead?: number, siteId?: number, vehicleTypeId?: number) =>
    request<DashboardOverview>(
      `/dashboard/overview${toQueryString({ asOfDate, monthsAhead, siteId, vehicleTypeId })}`,
    ),

  // ---------------------------------------------------------------
  // Email intake
  // ---------------------------------------------------------------

  /**
   * Whether a mailbox is connected, and to what.
   *
   * The screen asks first so it can say "no mailbox is connected" rather than
   * showing an empty queue, which looks identical to a working mailbox with no
   * mail and is the wrong thing to tell somebody waiting on a certificate.
   */
  getIntakeStatus: () => request<IntakeStatusResponse>('/intake/status'),

  getIntakeQueue: (page = 1, pageSize = 50) =>
    request<IntakePage>(`/intake${toQueryString({ page, pageSize })}`),

  /** Pending and unmatched counts, for the header badge. */
  getIntakeCount: () => request<{ pending: number; unmatched: number }>('/intake/count'),

  /**
   * Confirm an arrival.
   *
   * Everything the reviewer can see is sent back, not just what they changed:
   * what gets filed must be what they looked at and agreed to. rowVersion comes
   * from the queue read and is what stops two reviewers filing the same
   * certificate twice.
   */
  acceptIntake: (intakeId: number, body: AcceptIntakeRequest) =>
    request<{ complianceDocumentId: number }>(`/intake/${intakeId}/accept`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  rejectIntake: (intakeId: number, reason: string, rowVersion: string) =>
    request<void>(`/intake/${intakeId}/reject`, {
      method: 'POST',
      body: JSON.stringify({ reason, rowVersion }),
    }),

  /** Read the mailbox now rather than waiting out the poll interval. */
  pollIntake: () => request<Schemas['PollOutcome']>('/intake/poll', { method: 'POST' }),

  /**
   * The arrival's bytes as a blob URL, so the reviewer can read the scan beside
   * the fields. Same reasoning as fetchDocumentBlobUrl: a browser navigating to
   * a URL sends no Authorization header.
   *
   * The caller must revokeObjectURL when finished.
   */
  fetchIntakeBlobUrl: async (intakeId: number) => {
    const token = await getAccessToken()
    const response = await fetch(`${BASE}/intake/${intakeId}/content`, {
      credentials: 'same-origin',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })

    if (!response.ok) {
      throw new ApiError(response.status, 'Could not open that attachment.')
    }

    return URL.createObjectURL(await response.blob())
  },

  /** The report workbook, fetched with the caller's token. Same reasoning as above. */
  fetchReportWorkbook: async (vehicleIds: number[]) => {
    const token = await getAccessToken()
    const response = await fetch(
      `${BASE}/reports/vehicles.xlsx${toQueryString({ ids: vehicleIds.join(',') })}`,
      { headers: token ? { Authorization: `Bearer ${token}` } : {} },
    )

    if (!response.ok) {
      throw new ApiError(response.status, 'Could not build the workbook.')
    }

    return URL.createObjectURL(await response.blob())
  },
}
